import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";

type FleetTab = "standard" | "executive";

type NoticeRow = {
  vehicleTypeId: string;
  description: string;
  seatCapacity: number | null;
  noticeHours: number;
  noticeLabel: string;
};

type VehicleOption = {
  id: string;
  description: string;
  seatCapacity: number | null;
};

type PltRow = {
  id: string;
  referenceNumber: number;
  pickupPostcodes: string;
  noticeType: string;
  noticeHours: number;
  vehicleTypeIds: string[];
  carSizesLabel: string;
  status: boolean;
};

const NOTICE_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 24, 48, 72];

function vehicleLabel(row: NoticeRow): string {
  const d = row.description.toLowerCase();
  const cap = row.seatCapacity;
  if (cap != null && cap <= 4 && d.includes("estate")) return "1-4 PASSENGERS (ESTATE)";
  if (cap != null && cap <= 4) return "1-4 PASSENGERS";
  if (cap != null && cap <= 6) return "5-6 PASSENGERS";
  if (cap === 7) return "7 PASSENGERS";
  if (cap === 8) return "8 PASSENGERS";
  if (cap === 9) return "9 PASSENGERS";
  if (cap != null && cap <= 14) return "10-14 PASSENGERS";
  if (cap != null && cap >= 15) return "15-16 PASSENGERS";
  return row.description.toUpperCase();
}

function formatNotice(hours: number): string {
  const h = Math.max(1, Math.floor(hours));
  return `${h} HOUR${h === 1 ? "" : "S"}' NOTICE`;
}

const emptyPlt = {
  pickupPostcodes: "",
  noticeType: "Minimum notice",
  noticeHours: "12",
  vehicleTypeIds: [] as string[],
};

export default function NoticePeriods() {
  const [fleetTab, setFleetTab] = useState<FleetTab>("standard");
  const [rows, setRows] = useState<NoticeRow[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleOption[]>([]);
  const [plts, setPlts] = useState<PltRow[]>([]);
  const [pltForm, setPltForm] = useState(emptyPlt);
  const [editingPltId, setEditingPltId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pltSaving, setPltSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [noticeRes, pltRes, typesRes] = await Promise.all([
        axiosInstance.get<NoticeRow[]>("/notice-periods", {
          params: { fleetCategory: fleetTab, fuelCategory: "petrol_diesel_hybrid" },
        }),
        axiosInstance.get<PltRow[]>("/notice-periods/postcode-lead-times", {
          params: { fleetCategory: fleetTab, fuelCategory: "petrol_diesel_hybrid" },
        }),
        axiosInstance.get<VehicleOption[]>("/postcode-area-prices/vehicle-types", {
          params: { fleetCategory: fleetTab },
        }),
      ]);
      setRows(noticeRes.data);
      setPlts(pltRes.data);
      setVehicleTypes(typesRes.data);
    } catch {
      setMessage("Failed to load notice periods");
    } finally {
      setLoading(false);
    }
  }, [fleetTab]);

  useEffect(() => {
    load();
  }, [load]);

  const updateNotice = (vehicleTypeId: string, hours: number) => {
    setRows((prev) =>
      prev.map((r) =>
        r.vehicleTypeId === vehicleTypeId
          ? { ...r, noticeHours: hours, noticeLabel: formatNotice(hours) }
          : r,
      ),
    );
  };

  const saveNotices = async () => {
    setSaving(true);
    setMessage("");
    try {
      await axiosInstance.put("/notice-periods", {
        fleetCategory: fleetTab,
        fuelCategory: "petrol_diesel_hybrid",
        rows: rows.map((r) => ({
          vehicleTypeId: r.vehicleTypeId,
          noticeHours: r.noticeHours,
        })),
      });
      setMessage("Notice periods saved");
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const resetPltForm = () => {
    setPltForm(emptyPlt);
    setEditingPltId(null);
  };

  const savePlt = async (e: React.FormEvent) => {
    e.preventDefault();
    setPltSaving(true);
    try {
      const payload = {
        fleetCategory: fleetTab,
        fuelCategory: "petrol_diesel_hybrid",
        pickupPostcodes: pltForm.pickupPostcodes,
        noticeType: pltForm.noticeType,
        noticeHours: Number(pltForm.noticeHours),
        vehicleTypeIds: pltForm.vehicleTypeIds,
        status: true,
      };
      if (editingPltId) {
        await axiosInstance.put(`/notice-periods/postcode-lead-times/${editingPltId}`, payload);
      } else {
        await axiosInstance.post("/notice-periods/postcode-lead-times", payload);
      }
      resetPltForm();
      await load();
      setMessage("Postcode lead time saved");
    } catch {
      setMessage("Failed to save postcode lead time");
    } finally {
      setPltSaving(false);
    }
  };

  const startEditPlt = (row: PltRow) => {
    setEditingPltId(row.id);
    setPltForm({
      pickupPostcodes: row.pickupPostcodes,
      noticeType: row.noticeType,
      noticeHours: String(row.noticeHours),
      vehicleTypeIds: row.vehicleTypeIds,
    });
  };

  const deletePlt = async (id: string) => {
    if (!window.confirm("Delete this postcode lead time?")) return;
    await axiosInstance.delete(`/notice-periods/postcode-lead-times/${id}`);
    await load();
  };

  const toggleVehicleOnPlt = (id: string) => {
    setPltForm((f) => {
      const has = f.vehicleTypeIds.includes(id);
      return {
        ...f,
        vehicleTypeIds: has
          ? f.vehicleTypeIds.filter((x) => x !== id)
          : [...f.vehicleTypeIds, id],
      };
    });
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageBreadcrumb pageTitle="Notice Periods" />
        <div className="flex gap-3 text-sm">
          <Link to="/vehicle-type" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
            Add Fleet Type
          </Link>
          <span className="text-gray-300">|</span>
          <a
            href="https://www.gov.uk/find-postcode"
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
          >
            Help
          </a>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 max-w-4xl">
        You will only appear for searches where you have sufficient notice to reach the pickup
        point. Below are minimum notice periods (excluding drive time) for each vehicle size.
        Postcode lead times override these when the pickup matches an area.
      </p>

      <div className="mt-4 flex gap-2 border-b">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium border-b-2 -mb-px border-brand-500 text-brand-600 dark:text-brand-400"
        >
          Petrol, Diesel &amp; Hybrid
        </button>
      </div>

      <div className="mt-2 flex gap-2 border-b">
        {(["standard", "executive"] as FleetTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setFleetTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              fleetTab === tab
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-8 text-gray-500">Loading...</p>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {rows.map((row) => (
              <div
                key={row.vehicleTypeId}
                className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] p-4 flex flex-col items-center text-center"
              >
                <div className="w-14 h-10 mb-2 flex items-center justify-center text-blue-500">
                  <svg viewBox="0 0 64 32" className="w-full h-full" aria-hidden>
                    <path
                      fill="currentColor"
                      d="M4 20h40l-4-10H14L10 20zm44 0h8l-4-14h-6l2 14zM12 24a4 4 0 110-8 4 4 0 010 8zm32 0a4 4 0 110-8 4 4 0 010 8z"
                    />
                  </svg>
                </div>
                <p className="text-xs font-semibold text-gray-800 leading-tight mb-3 min-h-[2.5rem]">
                  {vehicleLabel(row)}
                </p>
                <div className="relative">
                  <select
                    className="appearance-none w-28 h-28 rounded-full border-4 border-blue-500 bg-white text-center text-xs font-bold text-blue-700 px-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300"
                    value={row.noticeHours}
                    onChange={(e) =>
                      updateNotice(row.vehicleTypeId, Number(e.target.value))
                    }
                    aria-label={`Notice hours for ${vehicleLabel(row)}`}
                  >
                    {NOTICE_OPTIONS.map((h) => (
                      <option key={h} value={h}>
                        {formatNotice(h)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-4">
            <button
              type="button"
              onClick={saveNotices}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save notice periods"}
            </button>
          </div>

          <section className="mt-10">
            <h2 className="text-lg font-semibold text-gray-800">
              Postcode Lead Times (PLTs)
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Require longer notice for pickups in specific postcode areas.
            </p>

            <form
              onSubmit={savePlt}
              className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] p-4 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup area (postcodes)
                </label>
                <input
                  className="w-full border rounded-lg px-3 py-2 uppercase"
                  placeholder="e.g. TW6, LHR"
                  value={pltForm.pickupPostcodes}
                  onChange={(e) =>
                    setPltForm((f) => ({ ...f, pickupPostcodes: e.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notice type
                </label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={pltForm.noticeType}
                  onChange={(e) =>
                    setPltForm((f) => ({ ...f, noticeType: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notice (hours)
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={pltForm.noticeHours}
                  onChange={(e) =>
                    setPltForm((f) => ({ ...f, noticeHours: e.target.value }))
                  }
                >
                  {NOTICE_OPTIONS.map((h) => (
                    <option key={h} value={h}>
                      {h} hours
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Car sizes (leave empty for all)
                </label>
                <div className="flex flex-wrap gap-2">
                  {vehicleTypes.map((v) => (
                    <label
                      key={v.id}
                      className={`text-xs px-2 py-1 rounded border cursor-pointer ${
                        pltForm.vehicleTypeIds.includes(v.id)
                          ? "bg-blue-50 border-blue-400 text-blue-800"
                          : "border-gray-200 text-gray-600"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={pltForm.vehicleTypeIds.includes(v.id)}
                        onChange={() => toggleVehicleOnPlt(v.id)}
                      />
                      {v.description}
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button
                  type="submit"
                  disabled={pltSaving}
                  className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {pltSaving ? "Saving..." : editingPltId ? "Update PLT" : "+ Add PLT"}
                </button>
                {editingPltId && (
                  <button
                    type="button"
                    onClick={resetPltForm}
                    className="px-4 py-2 border rounded-lg text-sm text-gray-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left">Pickup area</th>
                    <th className="px-4 py-2 text-left">Notice type</th>
                    <th className="px-4 py-2 text-left">Car sizes</th>
                    <th className="px-4 py-2 text-left">Hours</th>
                    <th className="px-4 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {plts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                        No postcode lead times yet
                      </td>
                    </tr>
                  ) : (
                    plts.map((row) => (
                      <tr key={row.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs uppercase">
                          {row.pickupPostcodes}
                        </td>
                        <td className="px-4 py-3">{row.noticeType}</td>
                        <td className="px-4 py-3 text-gray-600">{row.carSizesLabel}</td>
                        <td className="px-4 py-3">{row.noticeHours}h</td>
                        <td className="px-4 py-3 text-right space-x-3">
                      <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                            onClick={() => startEditPlt(row)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-red-600 hover:underline"
                            onClick={() => deletePlt(row.id)}
                          >
                            Delete
                          </button>
                      </div>
                    </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {message && (
            <p
              className={`mt-4 text-sm ${
                message.includes("Failed") || message.includes("failed")
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            >
              {message}
            </p>
          )}
        </>
      )}
    </div>
  );
}
